import React from "react";
import { Button } from "../../ui";

export interface Campaign {
  id: number;
  name: string;
  setting: string;
  type: string;
  description?: string;
  world?: string;
  isPublic?: boolean;
  maxPlayers?: number;
  users?: Array<{
    id: number;
    username: string;
    name: string;
    email: string;
  }>;
}

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "danger";
}

export interface CampaignCardProps {
  campaign: Campaign;
  actions?: ActionMenuItem[];
  variant?: "default" | "compact" | "detailed";
  showActions?: boolean;
  showStats?: boolean;
  onClick?: () => void;
  className?: string;
}

const CampaignCard: React.FC<CampaignCardProps> = ({
  campaign,
  actions = [],
  variant = "default",
  showActions = true,
  showStats = true,
  onClick,
  className = "",
}) => {
  const playerCount = campaign.users?.length || 0;
  const maxPlayers = campaign.maxPlayers || 4;

  const baseClasses =
    "bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-yellow-500 transition-colors";
  const clickableClasses = onClick ? "cursor-pointer" : "";
  const variantClasses = {
    default: "space-y-3",
    compact: "space-y-2",
    detailed: "space-y-4",
  };

  return (
    <div
      className={`${baseClasses} ${clickableClasses} ${variantClasses[variant]} ${className}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-yellow-400 truncate">{campaign.name}</h3>
          <div className="flex items-center space-x-2 text-sm text-gray-400 mt-1">
            <span>{campaign.type}</span>
            <span>•</span>
            <span>{campaign.setting}</span>
          </div>
        </div>

        {/* Public/Private Badge */}
        <div className="ml-2">
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              campaign.isPublic ? "bg-green-800 text-green-200" : "bg-gray-700 text-gray-300"
            }`}
          >
            {campaign.isPublic ? "Public" : "Private"}
          </span>
        </div>
      </div>

      {/* World Info */}
      {campaign.world && variant !== "compact" && (
        <div className="text-sm text-gray-300">
          <span className="text-gray-500">World:</span> {campaign.world}
        </div>
      )}

      {/* Description */}
      {campaign.description && variant === "detailed" && (
        <div className="text-sm text-gray-300">
          <p className="line-clamp-2">{campaign.description}</p>
        </div>
      )}

      {/* Stats */}
      {showStats && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-1">
              <span className="text-gray-500">Players:</span>
              <span className="text-yellow-400 font-medium">
                {playerCount}/{maxPlayers}
              </span>
            </div>
          </div>

          {/* Progress Bar for Players */}
          {variant !== "compact" && (
            <div className="flex-1 max-w-24 ml-4">
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className="bg-yellow-500 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min((playerCount / maxPlayers) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {showActions && actions.length > 0 && (
        <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-700">
          {actions.map((action, index) => (
            <Button
              key={index}
              variant={action.variant || "secondary"}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignCard;
